import Button from '@jetbrains/ring-ui/components/button/button'
import DropdownMenu from '@jetbrains/ring-ui/components/dropdown-menu/dropdown-menu'
import type {ListDataItem} from '@jetbrains/ring-ui/components/list/consts'
import plur from 'plur'
import type {SyntheticEvent} from 'react'
import {Suspense, useContext, useEffect, useReducer} from 'react'
import {shallowEqual} from 'react-redux'

import {openDialog} from '../../../../actions'
import {BuildSelectionIdContext} from '../../../../contexts/buildSelection'
import {useUpdateResults} from '../../../../contexts/update'
import {getAddTagsArg} from '../../../../rest/builds'
import {getCompareBuildHref} from '../../../../routes'
import {restApi} from '../../../../services/rest'
import {buildSelections} from '../../../../slices/buildSelections'
import type {BuildId, BuildTypeId} from '../../../../types'
import {DialogType, KillOperationKind, STAR_TAG} from '../../../../types'
import {BS} from '../../../../types/BS_types'
import {emptyArray} from '../../../../utils/empty'
import {notNull} from '../../../../utils/guards'
import {useIsComparable} from '../../../pages/CompareBuildsPage/ChooseCompareBuilds/ChooseCompareBuilds.hooks'
import CommentDialog from '../../BuildActionsDropdown/CommentDialog/CommentDialog.container'
import PinDialog from '../../BuildActionsDropdown/PinDialog/PinDialog.container'
import TagDialog from '../../BuildActionsDropdown/TagDialog/TagDialog.container'
import IconButton from '../../IconButton/IconButton'
import ServiceMessage from '../../ServiceMessage/ServiceMessage'
import {useAvailableActions} from '../Builds.hooks'
import {BuildBatchAction, BuildBatchActionsOrder} from '../Builds.utils'

import styles from './BuildBatchActions.css'

import {useAppDispatch, useAppSelector} from 'src/hooks/react-redux'

type BuildBatchActionsProps = {
  buildTypeId?: BuildTypeId
  message?: boolean
}

type SetAvailableActionsPayload = {
  buildId: BuildId
  actions: Record<BuildBatchAction, boolean | undefined>
}

type ActionAvailabilityDetectorProps = {
  buildId: BuildId
  setAvailableActionsForBuild: (payload: SetAvailableActionsPayload) => void
}

function ActionAvailabilityDetector({
  buildId,
  setAvailableActionsForBuild,
}: ActionAvailabilityDetectorProps) {
  const actions = useAvailableActions(buildId)
  useEffect(() => {
    setAvailableActionsForBuild({buildId, actions})
  })
  return null
}

function BuildBatchActions({message, buildTypeId}: BuildBatchActionsProps) {
  const dispatch = useAppDispatch()
  const [addBuildsTags] = restApi.endpoints.addTagsToMultipleBuilds.useMutation()
  const selectionId = useContext(BuildSelectionIdContext)
  const selection = useAppSelector(state => state.buildSelections[selectionId]) ?? []
  const update = useUpdateResults()
  const isComparable = useIsComparable()
  const [availableActionsMap, setAvailableActionsForBuild] = useReducer(
    (
      prevState: Record<BuildId, Record<BuildBatchAction, boolean | undefined>>,
      {buildId, actions}: SetAvailableActionsPayload,
    ) =>
      shallowEqual(prevState[buildId], actions)
        ? prevState
        : {
            ...prevState,
            [buildId]: actions,
          },
    {},
  )
  const availableActions = BuildBatchActionsOrder.filter(action =>
    selection.every(id => availableActionsMap[id]?.[action]),
  )
  if (selection.length === 0) {
    return null
  }

  const clear = () =>
    dispatch(buildSelections.actions.setSelection({selectionId, selection: emptyArray}))
  const items: ListDataItem[] = [
    isComparable && selection.length === 2
      ? {
          label: 'Compare',
          href: `${getCompareBuildHref(selection[0])}?withBuildId=${selection[1]}`,
        }
      : null,
    ...availableActions.map(action => {
      switch (action) {
        case BuildBatchAction.REMOVE_FROM_QUEUE:
          return {
            label: 'Remove from queue...',
            onClick: () =>
              BS?.StopBuildDialog?.showStopBuildDialog(
                selection,
                '',
                KillOperationKind.CANCEL,
              ).then(() => {
                update()
                clear()
              }),
          }
        case BuildBatchAction.STOP:
          return {
            label: 'Stop...',
            onClick: () =>
              BS?.StopBuildDialog?.showStopBuildDialog(selection, '', KillOperationKind.STOP).then(
                () => {
                  update()
                  clear()
                },
              ),
          }
        case BuildBatchAction.REMOVE:
          return {
            label: 'Remove...',
            onClick: () =>
              BS?.StopBuildDialog?.showStopBuildDialog(
                selection,
                '',
                KillOperationKind.REMOVE,
              ).then(() => {
                update()
                clear()
              }),
          }
        case BuildBatchAction.PIN:
          return {
            label: 'Pin...',
            onClick: () => dispatch(openDialog(selection.join(','), DialogType.PIN)),
          }
        case BuildBatchAction.UNPIN:
          return {
            label: 'Unpin...',
            onClick: () => dispatch(openDialog(selection.join(','), DialogType.PIN)),
          }
        case BuildBatchAction.ADD_TAGS:
          return {
            label: 'Add tags...',
            onClick: () => dispatch(openDialog(selection.join(','), DialogType.TAGS)),
          }
        case BuildBatchAction.COMMENT:
          return {
            label: 'Add comment...',
            onClick: () => dispatch(openDialog(selection.join(','), DialogType.COMMENT)),
          }
        case BuildBatchAction.ADD_TO_FAVORITES:
          return {
            label: 'Add to favorites',
            onClick: () =>
              addBuildsTags(
                getAddTagsArg(selection, [
                  {
                    name: STAR_TAG,
                    private: true,
                  },
                ]),
              ),
          }
        default:
          return null
      }
    }),
  ].filter(notNull)

  const controls =
    items.length > 0 ? (
      <div className={styles.actions}>
        <Button
          primary
          onClick={(e: SyntheticEvent) => items[0].onClick?.(items[0], e)}
          href={items[0].href}
        >
          {items[0].label}
        </Button>
        {items.length === 2 && (
          <Button
            className={styles.secondary}
            text
            onClick={(e: SyntheticEvent) => items[1].onClick?.(items[1], e)}
            href={items[1].href}
          >
            {items[1].label}
          </Button>
        )}
        {items.length > 2 && (
          <DropdownMenu
            anchor="More"
            data={items.slice(1)}
            menuProps={{className: styles.popupMenu}}
          />
        )}
        <PinDialog buildTypeId={buildTypeId} buildIds={selection} />
        <TagDialog buildTypeId={buildTypeId} buildIds={selection} />
        <CommentDialog buildIds={selection} />
      </div>
    ) : null
  return (
    <Suspense>
      {selection.map(buildId => (
        <ActionAvailabilityDetector
          key={buildId}
          buildId={buildId}
          setAvailableActionsForBuild={setAvailableActionsForBuild}
        />
      ))}
      {items.length === 0 && selection.length === 1 ? null : message ? (
        <ServiceMessage
          closeable={false}
          icon="task"
          controls={controls}
          title={
            <>
              {`${selection.length} ${plur('Build', selection.length)} Selected`}
              <IconButton className={styles.close} icon="close" onClick={clear} />
            </>
          }
        >
          {items.length === 0 &&
            'No common actions available for current selection. Try deselecting some builds'}
        </ServiceMessage>
      ) : (
        controls
      )}
    </Suspense>
  )
}

export default BuildBatchActions
